// Drawing and encoding pictures. The browser version uses createImageBitmap and an OffscreenCanvas
// where there is one, and a page canvas where there is not. Tests pass their own Raster, so the
// photo pipeline runs on real pixels outside a browser.

/** Something that can be drawn: an ImageBitmap, a video frame or a canvas. */
export type Drawable = { readonly width: number; readonly height: number };

export interface Picture extends Drawable {
  readonly source: unknown;
  close(): void;
}

export interface Ctx2d {
  imageSmoothingEnabled: boolean;
  imageSmoothingQuality: "low" | "medium" | "high";
  fillStyle: unknown;
  fillRect(x: number, y: number, w: number, h: number): void;
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void;
  drawImage(source: never, dx: number, dy: number, dw: number, dh: number): void;
  getImageData(sx: number, sy: number, sw: number, sh: number): { data: Uint8ClampedArray };
}

export interface Surface extends Drawable {
  readonly source: unknown;
  readonly ctx: Ctx2d;
  encode(type: string, quality?: number): Promise<Blob>;
}

export interface Raster {
  /**
   * Decodes a file to pixels. Callers remove a JPEG's EXIF block first, because browsers disagree
   * on whether they turn the picture, so the pixels arrive as stored everywhere.
   */
  decode(file: Blob): Promise<Picture>;
  /** A picture from RGBA pixels, as the HEIC decoder gives them. */
  fromPixels(data: Uint8ClampedArray, width: number, height: number): Promise<Picture>;
  surface(width: number, height: number): Surface;
  /** Whether encode can produce this type. */
  canEncode(type: string): Promise<boolean>;
}

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;

function makeCanvas(width: number, height: number): AnyCanvas {
  if (typeof OffscreenCanvas !== "undefined") {
    const c = new OffscreenCanvas(width, height);
    if (typeof c.convertToBlob === "function") return c;
  }
  const c = document.createElement("canvas");
  c.width = width;
  c.height = height;
  return c;
}

function encodeCanvas(canvas: AnyCanvas, type: string, quality?: number): Promise<Blob> {
  if ("convertToBlob" in canvas) return canvas.convertToBlob({ type, quality });
  return new Promise((resolve, reject) =>
    (canvas as HTMLCanvasElement).toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("This picture could not be encoded."))),
      type,
      quality,
    ),
  );
}

const encodable = new Map<string, Promise<boolean>>();

export const browserRaster: Raster = {
  async decode(file) {
    const bitmap = await createImageBitmap(file);
    return { width: bitmap.width, height: bitmap.height, source: bitmap, close: () => bitmap.close() };
  },
  async fromPixels(data, width, height) {
    const bitmap = await createImageBitmap(
      new ImageData(data as Uint8ClampedArray<ArrayBuffer>, width, height),
    );
    return { width, height, source: bitmap, close: () => bitmap.close() };
  },
  surface(width, height) {
    const canvas = makeCanvas(width, height);
    const ctx = canvas.getContext("2d") as unknown as Ctx2d | null;
    if (!ctx) throw new Error("This device cannot draw pictures.");
    return {
      width,
      height,
      source: canvas,
      ctx,
      encode: (type, quality) => encodeCanvas(canvas, type, quality),
    };
  },
  canEncode(type) {
    let known = encodable.get(type);
    if (!known) {
      // Browsers that cannot encode a type quietly give PNG instead.
      known = encodeCanvas(makeCanvas(1, 1), type, 0.8)
        .then((b) => b.type === type)
        .catch(() => false);
      encodable.set(type, known);
    }
    return known;
  },
};
